import { z } from "zod";
import type { SiteDraftRequest, SiteResponse } from "./siteApi";

/*
 * Pure, framework-free helpers for the site editor (unit-testable without React): the WCAG contrast
 * guard (a NON-blocking brand-colour warning, RGAA posture), EUR minor-unit price conversion (prices
 * are always integer minor units on the wire, ADR-0024), and the form ⇄ request mapping. The SERVER is
 * authoritative — these mirror its rules so the manager gets instant feedback, never replace it.
 */

const HEX = /^#[0-9a-fA-F]{6}$/;

export function isHexColor(value: string): boolean {
  return HEX.test(value);
}

export function isHttpsUrl(value: string): boolean {
  return value.startsWith("https://") && value.length > "https://".length;
}

// --- WCAG contrast (brand-colour warning) ------------------------------------------------------

function channel(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG 2.1 contrast ratio (1..21) between two `#RRGGBB` colours. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

const WHITE = "#ffffff";
const AA_NORMAL = 4.5;

/**
 * True when [primaryColor] fails WCAG AA (4.5:1) as a BUTTON BACKGROUND under the template's fixed
 * white foreground (white-on-brand is the template's button pairing) — i.e. a pale brand colour that
 * white text cannot sit on accessibly. NON-blocking: the editor shows a warning and still saves the
 * venue's real brand colour (the accessible fallback pairing is the template's side,
 * `tasks/venue-site-template.md`). The template's exact foreground set is its contract; this mirrors
 * the common case so the manager gets an instant, honest heads-up.
 */
export function contrastWarning(primaryColor: string | null | undefined): boolean {
  if (!primaryColor || !isHexColor(primaryColor)) return false;
  return contrastRatio(primaryColor, WHITE) < AA_NORMAL;
}

// --- EUR minor units ---------------------------------------------------------------------------

/** Minor units → an editable euro string (`1250` → `"12.50"`, `null` → `""`). */
export function minorToEuroInput(minor: number | null | undefined): string {
  if (minor === null || minor === undefined) return "";
  return (minor / 100).toFixed(2);
}

/** Euro input → integer minor units (`"12,50"`/`"12.50"` → `1250`; blank → null; non-numeric → null). */
export function euroInputToMinor(input: string): number | null {
  const trimmed = input.trim().replace(",", ".");
  if (trimmed === "") return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value * 100);
}

// --- gallery order (cover first) ---------------------------------------------------------------

/** Move the item at [index] by [delta] (keyboard-operable reorder, not drag-only). Cover = index 0. */
export function moveInList<T>(list: readonly T[], index: number, delta: number): T[] {
  const next = [...list];
  const target = index + delta;
  if (index < 0 || index >= next.length || target < 0 || target >= next.length) return next;
  const a = next[index] as T;
  const b = next[target] as T;
  next[index] = b;
  next[target] = a;
  return next;
}

// --- form model --------------------------------------------------------------------------------

export type MenuMode = "NONE" | "STRUCTURED" | "DOCUMENTS";

export interface MenuItemValue {
  name: string;
  description: string;
  price: string;
}
export interface MenuSectionValue {
  name: string;
  items: MenuItemValue[];
}
export interface SocialValue {
  platform: string;
  url: string;
}

export interface SiteFormValues {
  description: string;
  cuisineTypes: string;
  priceRange: number;
  primaryColor: string;
  logoAssetId: string;
  contactPhone: string;
  contactEmail: string;
  contactWebsite: string;
  menuMode: MenuMode;
  menuDocumentIds: string;
  sections: MenuSectionValue[];
  socialLinks: SocialValue[];
  galleryOrder: string[];
}

const csv = (value: string): string[] =>
  value
    .split(",")
    .map((v) => v.trim())
    .filter((v) => v.length > 0);

export function siteFormSchema(t: (key: string) => string) {
  return z
    .object({
      description: z.string().max(4000).optional().default(""),
      cuisineTypes: z.string().optional().default(""),
      priceRange: z.coerce.number().int().min(0).max(4).default(0),
      primaryColor: z.string().optional().default(""),
      logoAssetId: z.string().optional().default(""),
      contactPhone: z.string().optional().default(""),
      contactEmail: z.string().optional().default(""),
      contactWebsite: z.string().optional().default(""),
      menuMode: z.enum(["NONE", "STRUCTURED", "DOCUMENTS"]).default("NONE"),
      menuDocumentIds: z.string().optional().default(""),
      sections: z
        .array(
          z.object({
            name: z.string(),
            items: z.array(
              z.object({ name: z.string(), description: z.string(), price: z.string() }),
            ),
          }),
        )
        .default([]),
      socialLinks: z.array(z.object({ platform: z.string(), url: z.string() })).default([]),
      galleryOrder: z.array(z.string()).default([]),
    })
    .superRefine((v, ctx) => {
      if (v.primaryColor !== "" && !isHexColor(v.primaryColor)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["primaryColor"],
          message: t("site.error.hexColor"),
        });
      }
      if (v.contactWebsite !== "" && !isHttpsUrl(v.contactWebsite)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["contactWebsite"],
          message: t("site.error.https"),
        });
      }
      v.socialLinks.forEach((s, i) => {
        if (s.url !== "" && !isHttpsUrl(s.url)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["socialLinks", i, "url"],
            message: t("site.error.https"),
          });
        }
      });
    });
}

export type SiteFormShape = z.input<ReturnType<typeof siteFormSchema>>;

/** Map a loaded site's DRAFT + branding into editable form values. */
export function formFromSite(site: SiteResponse): SiteFormValues {
  const d = site.draft;
  const menuMode: MenuMode = d.menu === null ? "NONE" : d.menu.mode;
  return {
    description: d.description ?? "",
    cuisineTypes: d.cuisineTypes.join(", "),
    priceRange: d.priceRange ?? 0,
    primaryColor: site.branding.primaryColor ?? "",
    logoAssetId: site.branding.logoAssetId ?? "",
    contactPhone: d.contact.phone ?? "",
    contactEmail: d.contact.email ?? "",
    contactWebsite: d.contact.website ?? "",
    menuMode,
    menuDocumentIds: (d.menu?.assetIds ?? []).join(", "),
    sections: (d.menu?.sections ?? []).map((s) => ({
      name: s.name,
      items: s.items.map((i) => ({
        name: i.name,
        description: i.description ?? "",
        price: minorToEuroInput(i.priceMinor),
      })),
    })),
    socialLinks: d.socialLinks.map((s) => ({ platform: s.platform, url: s.url })),
    galleryOrder: [...d.galleryOrder],
  };
}

/** Build the PATCH body from the form values + the CAS version. */
export function buildDraftRequest(values: SiteFormValues, version: number): SiteDraftRequest {
  let menu: SiteDraftRequest["menu"] = null;
  if (values.menuMode === "STRUCTURED") {
    menu = {
      mode: "STRUCTURED",
      sections: values.sections.map((s) => ({
        name: s.name.trim(),
        items: s.items
          .filter((i) => i.name.trim() !== "")
          .map((i) => ({
            name: i.name.trim(),
            description: i.description.trim() || null,
            priceMinor: euroInputToMinor(i.price),
            currency: euroInputToMinor(i.price) === null ? null : "EUR",
          })),
      })),
    };
  } else if (values.menuMode === "DOCUMENTS") {
    menu = { mode: "DOCUMENTS", assetIds: csv(values.menuDocumentIds) };
  }
  return {
    version,
    description: values.description.trim() || null,
    cuisineTypes: csv(values.cuisineTypes),
    priceRange: values.priceRange === 0 ? null : values.priceRange,
    menu,
    galleryOrder: values.galleryOrder,
    contact: {
      phone: values.contactPhone.trim() || null,
      country: "FR",
      email: values.contactEmail.trim() || null,
      website: values.contactWebsite.trim() || null,
    },
    socialLinks: values.socialLinks
      .filter((s) => s.url.trim() !== "")
      .map((s) => ({ platform: s.platform, url: s.url.trim() })),
    branding: {
      primaryColor: values.primaryColor.trim() || null,
      logoAssetId: values.logoAssetId.trim() || null,
    },
  };
}
